<template>
  <div class="timeline-wrapper">
    <h1 class="main-title">What did I do? </h1>
    
    <!-- Fixed content area containing timeline and description -->
    <div class="content-area">
      <!-- Timeline section -->
      <div class="timeline-section">
        <div class="timeline-container">
          <!-- The gradient timeline line -->
          <div class="timeline-line"></div>
          <!-- Timeline dots with diagonal labels -->
          <div
            v-for="(event, index) in timelineEvents"
            :key="event.id"
            class="timeline-dot"
            :class="{ active: activeIndex === index }"
            :style="{ left: event.left + '%' }"
            @mouseenter="setActive(index)"
            @click="setActive(index)"
          >
            <div class="dot"></div>
            <div class="dot-above-label"><span>{{ event.label }}</span></div>
            <div class="dot-below-label"><span>{{ event.year }}</span></div>
          </div>
        </div>
      </div>

      <!-- Description section -->
      <div class="description-section">
        <transition name="scroll" mode="out-in">
          <div class="description-container" v-if="activeEvent" :key="activeEvent.id">
            <img v-if="activeEvent.image" :src="activeEvent.image" :alt="activeEvent.label + ' logo'" class="organization-logo" :style="{ '--glow-color': activeEvent.glowColorRgba }" />
            <p>
              <strong>{{ activeEvent.period }}</strong> – {{ activeEvent.description }}
            </p>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import nrgLogo from '@/assets/nrg-logo.png';
import kupLogo from '@/assets/kup-logo.png';
import taltechLogo from '@/assets/taltech-logo.png';
import foxwayLogo from '@/assets/foxway-logo.png';
import unitartuLogo from '@/assets/unitartu-logo.png';

export default {
  name: "TimelineView",
  data() {
    const events = [
      // To add a new event, simply add a new object to this array.
      // The 'left' position of the dot on the timeline is calculated automatically
      // based on the 'year' property, ensuring correct chronological spacing.
      // The 'id' should be unique.
      // The 'image' should be an imported asset (e.g., import myLogo from '@/assets/my-logo.png').
      // 'glowColor' is a hex code for the dot and logo glow effect.
      {
        id: 1,
        period: "2016–2019",
        year: 2016,
        label: "NRG",
        description:
          "I studied at Nõo Reaalgümnaasium, one of Estonia’s leading science-focused high schools, in a physics-intensive program that strengthened my math and analytical skills. That’s also where I wrote my first lines of Python code, which kicked off my interest in programming and solving problems through software.",
        image: nrgLogo,
        glowColor: '#4285F4' // Blue glow
      },
      {
        id: 2,
        period: "2019–2020",
        year: 2019,
        label: "EDF",
        description:
          "Served as a Delta Squad Leader in the Kuperjanov Infantry Battalion. Leading a team in demanding conditions taught me discipline, accountability, and effective teamwork. These experiences strengthened my leadership and communication skills, which I now bring to every technical project.",
        image: kupLogo,
        glowColor: '#FFFFFF' // White glow
      },
      {
        id: 3,
        period: "2020–2021",
        year: 2020,
        label: "TalTech",
        description:
          "After military service, I started my studies in Hardware Development and Programming at TalTech. My first year combined strong academic results with hands-on work in C/C++, embedded systems, and computer architecture. I also dove into networking and cybersecurity, building a solid understanding of how hardware and software come together in practice.",
        image: taltechLogo,
        glowColor: '#FF69B4' // Pink glow
      },
      {
        id: 4,
        period: "May 2021–Sep 2022",
        year: 2021,
        label: "Foxway",
        description:
          "Progressed from Junior to Senior Hardware Tester. Focused on hardware diagnostics, repair, and quality assurance. Led onboarding and mentoring efforts while improving workflows through automation and scripting across macOS and Windows environments.",
        image: foxwayLogo,
        glowColor: '#8E44AD' // Purple glow
      },
      {
        id: 5,
        period: "Aug 2022–Jun 2025",
        year: 2022,
        label: "UniTartu",
        description:"BSc in Computer Science. Specialized in software development and distributed systems, while completing a thesis on Causal Information Extraction Using Large Language Models. Built a strong base in both theoretical computer science and applied AI research.",
        image: unitartuLogo,
        glowColor: '#00BFFF' // Lighter-blue glow
      },
      {
        id: 6,
        period: "Aug 2025–Present",
        year: 2025,
        label: "TalTech",
        description: "Pursuing an MSc in Computer Science with a specialization in Machine Learning and AI. Focused on advanced model development, data processing, and applied AI techniques.",
        image: taltechLogo,
        glowColor: '#8E44AD' // Purple glow
      }
    ];

    // Dynamically compute the minimum and maximum years from the events.
    // This ensures the first event appears at 0% and the last at 100%.
    const years = events.map(event => event.year);
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);
    events.forEach(event => {
      event.left = ((event.year - minYear) / (maxYear - minYear)) * 100;
      event.glowColorRgba = this.hexToRgba(event.glowColor, 0.6); // Convert hex to RGBA with alpha 0.6
    });
    return {
      timelineEvents: events,
      activeIndex: 0
    };
  },
  computed: {
    activeEvent() {
      return this.timelineEvents[this.activeIndex];
    }
  },
  mounted() {
    // DISABLE HORIZONTAL SCROLLING WITH JAVASCRIPT
    this.disableHorizontalScroll();
    
    // UPDATE THEME COLOR FOR DYNAMIC ISLAND
    this.updateThemeColor();
    
    // Listen for color scheme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addListener(this.updateThemeColor);
    this.mediaQuery = mediaQuery; // Store reference for cleanup
  },
  beforeUnmount() {
    // Clean up event listeners
    this.enableHorizontalScroll();
    
    // Clean up theme color listener
    if (this.mediaQuery) {
      this.mediaQuery.removeListener(this.updateThemeColor);
    }
  },
  methods: {
    setActive(index) {
      this.activeIndex = index;
    },
    hexToRgba(hex, alpha = 1) {
      if (!hex) return `rgba(0, 0, 0, ${alpha})`;
      let r = 0, g = 0, b = 0;

      // Handle #RRGGBB or #RGB
      if (hex.length === 7) {
        r = parseInt(hex.substring(1, 3), 16);
        g = parseInt(hex.substring(3, 5), 16);
        b = parseInt(hex.substring(5, 7), 16);
      } else if (hex.length === 4) {
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
      }
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    updateThemeColor() {
      // Check if user prefers dark mode
      const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const themeColor = isDark ? '#121212' : '#ECF0F1';
      
      // Update or create theme-color meta tag
      let metaThemeColor = document.querySelector('meta[name="theme-color"]:not([media])');
      if (!metaThemeColor) {
        // Remove existing theme-color tags
        const existingTags = document.querySelectorAll('meta[name="theme-color"]');
        existingTags.forEach(tag => tag.remove());
        
        // Create new one
        metaThemeColor = document.createElement('meta');
        metaThemeColor.name = 'theme-color';
        document.head.appendChild(metaThemeColor);
      }
      metaThemeColor.content = themeColor;
      
      // Also update Apple status bar style
      let appleStatusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
      if (appleStatusBar) {
        appleStatusBar.content = isDark ? 'black-translucent' : 'default';
      }
      
      console.log(`Theme color updated to: ${themeColor} (${isDark ? 'dark' : 'light'} mode)`);
    },
    disableHorizontalScroll() {
      // Store original scroll position
      let lastScrollX = window.scrollX;
      
      // Prevent horizontal scrolling with scroll event
      const preventHorizontalScroll = () => {
        window.scrollTo(lastScrollX, window.scrollY);
      };
      
      // Prevent horizontal scrolling with wheel event
      const preventHorizontalWheel = (e) => {
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
          e.preventDefault();
        }
      };
      
      // Prevent horizontal touch scrolling
      let startX = 0;
      const preventHorizontalTouch = (e) => {
        if (e.touches.length === 1) {
          startX = e.touches[0].clientX;
        }
      };
      
      const preventHorizontalTouchMove = (e) => {
        if (e.touches.length === 1) {
          const moveX = e.touches[0].clientX;
          const diffX = startX - moveX;
          const diffY = Math.abs(e.touches[0].clientY - e.touches[0].clientY);
          
          // If horizontal movement is greater than vertical, prevent it
          if (Math.abs(diffX) > diffY) {
            e.preventDefault();
          }
        }
      };
      
      // Add event listeners
      window.addEventListener('scroll', preventHorizontalScroll, { passive: false });
      window.addEventListener('wheel', preventHorizontalWheel, { passive: false });
      window.addEventListener('touchstart', preventHorizontalTouch, { passive: false });
      window.addEventListener('touchmove', preventHorizontalTouchMove, { passive: false });
      
      // Store references for cleanup
      this.scrollListeners = {
        preventHorizontalScroll,
        preventHorizontalWheel,
        preventHorizontalTouch,
        preventHorizontalTouchMove
      };
    },
    enableHorizontalScroll() {
      // Remove event listeners
      if (this.scrollListeners) {
        window.removeEventListener('scroll', this.scrollListeners.preventHorizontalScroll);
        window.removeEventListener('wheel', this.scrollListeners.preventHorizontalWheel);
        window.removeEventListener('touchstart', this.scrollListeners.preventHorizontalTouch);
        window.removeEventListener('touchmove', this.scrollListeners.preventHorizontalTouchMove);
      }
    }
  }
};
</script>

<style scoped>
/* --- Color Mode Variables --- */
:root {
  --bg-color-light: #ECF0F1;
  --text-color-light: #424949;
  --bg-color-dark: #121212;
  --text-color-dark: #ffffff;
}
@media (prefers-color-scheme: dark) {
  :root {
    --bg-color: var(--bg-color-dark);
    --text-color: var(--text-color-dark);
  }
}
@media (prefers-color-scheme: light) {
  :root {
    --bg-color: var(--bg-color-light);
    --text-color: var(--text-color-light);
  }
}

/* --- Reset --- */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* SIMPLE GLOBAL HORIZONTAL SCROLL DISABLE */
html {
  overflow-x: hidden;
}

body {
  overflow-x: hidden;
}

/* --- Main Wrapper --- */
.timeline-wrapper {
  color: var(--text-color);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

/* --- Title --- */
.timeline-wrapper h1 {
  font-size: 2rem;
  margin-bottom: 40px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}
.timeline-wrapper h1 span {
  font-size: 1rem;
  color: var(--text-color);
}

/* --- Content Area (Timeline + Description) --- */
.content-area {
  width: 100%;
  height: 550px; /* Fixed total height prevents jumping */
  display: flex;
  flex-direction: column;
}

/* --- Timeline Section --- */
.timeline-section {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 140px; /* Fixed timeline height */
  flex-shrink: 0;
}

.timeline-container {
  position: relative;
  width: 100%;
  max-width: 1400px;
  cursor: pointer;
}

/* --- Gradient Timeline Line --- */
.timeline-line {
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 6px;
  background: linear-gradient(
    to right,
    #4285F4, #DB4437, #F4B400, #0F9D58, #4285F4
  );
  border-radius: 3px;
  transform: translateY(-50%);
  transition: transform 0.2s ease;
}
.timeline-container:hover .timeline-line {
  transform: translateY(-50%) scaleY(1.5);
}

/* --- Timeline Dots --- */
.timeline-dot {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  transition: transform 0.2s ease;
}
.timeline-dot .dot {
  width: 20px;
  height: 20px;
  background-color: #fff;
  border: 3px solid currentColor;
  border-radius: 50%;
  transition: transform 0.5s ease;
}
.timeline-dot:hover .dot,
.timeline-dot.active .dot {
  transform: scale(1.2);
  box-shadow: 0 0 15px currentColor;
}

.main-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 12px;
  background: linear-gradient(135deg, var(--text-color) 0%, #F517C1FF 50%, #F32136FF 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Dot labels */
.dot-above-label,
.dot-below-label {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.8rem;
  color: currentColor;
  white-space: nowrap;
  opacity: 0.5;
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.dot-above-label {
  top: -30px;
}

.dot-below-label {
  top: 35px;
}

.timeline-dot:hover .dot-above-label,
.timeline-dot.active .dot-above-label,
.timeline-dot:hover .dot-below-label,
.timeline-dot.active .dot-below-label {
  opacity: 1;
  transform: translateX(-50%) scale(1.1);
}

/* --- Description Section --- */
.description-section {
  width: 100%;
  flex: 1; /* Take remaining space after timeline */
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow: hidden; /* Prevent section from expanding */
}

.description-container {
  width: 100%;
  max-width: 600px;
  height: 100%; /* Use full available height */
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 20px;
  padding: 20px;
  overflow-y: auto; /* Scroll if content is too long */
}

.description-container p {
  flex: 1;
  margin: 0;
  padding: 0;
  text-align: left;
  font-size: 1.1rem;
  line-height: 1.6;
}

/* Logo styles */
.organization-logo {
  width: 80px;
  height: 80px;
  object-fit: contain;
  flex-shrink: 0;
  opacity: 1;
  transition: opacity 0.3s ease, transform 0.3s ease;
}

/* --- Scroll Transition --- */
.scroll-enter-active {
  animation: scrollIn 0.2s ease-in-out forwards;
}
.scroll-leave-active {
  animation: scrollOut 0.2s ease-in-out forwards;
}
@keyframes scrollIn {
  0% {
    transform: translateY(-20px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
@keyframes scrollOut {
  0% {
    transform: translateY(0);
    opacity: 1;
  }
  100% {
    transform: translateY(20px);
    opacity: 0;
  }
}

/* --- Mobile Adjustments --- */
@media (max-width: 768px) {
  .timeline-wrapper {
    padding: 0; /* Reset padding to allow h1 margin-top to control spacing */
    min-height: 100vh;
    justify-content: flex-start;
    padding-top: 0; /* Reset padding-top here as spacing will be controlled by h1 margin-top */
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-y; /* Only allow vertical panning */
  }
  
  .timeline-wrapper h1 {
    font-size: 1.6rem;
    margin-bottom: 25px;
    margin-top: 165px; /* Reduced from 180px - moved up 15px */
  }

  .timeline-wrapper h1 span {
    font-size: 0.8rem;
  }

  .content-area {
    height: auto;
    min-height: auto;
    display: flex;
    flex-direction: column;
    margin-top: 0; /* Reset margin-top to avoid conflicts */
    width: 100%;
  }

  .timeline-section {
    height: 100px;
    margin-bottom: 0px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 100%;
  }

  .timeline-container {
    height: 100px;
    padding: 0 10px;
    width: 81%; /* 90% of current 90% width - narrower on mobile */
    max-width: 81%;
    margin: 0 auto;
  }

  .timeline-line {
    height: 4px;
  }

  .timeline-dot {
    cursor: default;
  }

  .timeline-dot .dot {
    width: 14px;
    height: 14px;
    border-width: 2px;
  }

  .dot-above-label,
  .dot-below-label {
    font-size: 0.7rem;
  }

  .description-section {
    flex: 1;
    height: auto;
    max-height: auto;
    overflow-y: auto;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    width: 100%;
  }

  .description-container {
    flex-direction: column;
    align-items: center;
    height: auto;
    padding: 10px; /* Back to original padding */
    gap: 10px; /* Back to original gap */
    overflow-y: visible;
    overflow-x: visible; /* Allow glow to extend horizontally */
    max-width: 90%;
  }

  .description-container p {
    text-align: center;
    font-size: 0.95rem;
    line-height: 1.5;
    word-wrap: break-word; /* Ensure long words don't cause overflow */
    overflow-wrap: break-word; /* Modern browsers */
    hyphens: auto; /* Allow hyphenation if needed */
  }

  .description-container .organization-logo {
    width: 90px !important;
    height: 90px !important;
    margin: 5px; /* Reduced margin - just enough for small glow */
    /* Removed transform: translateX(-10px) to center properly */
  }

  /* Make Foxway logo smaller as requested */
  .description-container img[alt*="Foxway"] {
    width: 75px !important;
    height: 75px !important;
  }
}

/* --- Very small screens --- */
@media (max-width: 480px) {
  .timeline-wrapper {
    padding-top: 0; /* Reset padding-top here */
  }

  .timeline-wrapper h1 {
    font-size: 1.4rem;
    margin-bottom: 20px;
    margin-top: 135px; /* Reduced from 150px - moved up 15px */
  }

  .content-area {
    height: auto;
    margin-top: 0; /* Reset margin-top for very small screens */
  }

  .timeline-section {
    height: 90px;
  }

  .description-section {
    max-height: calc(100vh - 250px);
  }

  .description-container {
    padding: 10px; /* Back to original padding */
    max-width: 90%;
    overflow: visible; /* Allow glow to extend */
  }

  .description-container p {
    font-size: 0.9rem;
    word-wrap: break-word; /* Ensure long words don't cause overflow */
    overflow-wrap: break-word; /* Modern browsers */
    hyphens: auto; /* Allow hyphenation if needed */
  }

  .description-container .organization-logo {
    width: 70px !important;
    height: 70px !important;
    margin: 3px; /* Minimal margin for glow space */
  }

  /* Make Foxway logo smaller on very small screens too */
  .description-container img[alt*="Foxway"] {
    width: 60px !important;
    height: 60px !important;
  }
}
</style>